import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { ErrorMsg } from "./ErrorMsg";
import { IconButton } from "./IconButton";
import { cn } from "../../utils/lib";
import { Label } from "./Label";
import { Modal } from "./Modal";
import { SearchableDropdown } from "./SearchableDropdown";
import { Button } from "./Button";
import { IoCameraOutline } from "react-icons/io5";

interface FileUploadProps {
    value: string;
    onChange: (file: File | null) => void;
    accept?: string;
    label?: string;
    placeholder?: string;
    maxSizeMB?: number;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    errorMessage?: string;
    isError?: boolean;
    isDelete?: boolean;
    buttonLabel?: string | React.ReactNode;
    supportedFormatsText?: string;
    isCamera?: boolean;
}

const FileUpload = ({
    value,
    onChange,
    accept = "image/*,.pdf,.doc,.docx",
    label = "Upload File",
    placeholder = "No file selected",
    maxSizeMB = 5,
    disabled = false,
    required = false,
    className = "",
    errorMessage = "",
    isError = false,
    isDelete = false,
    buttonLabel = "Browse",
    supportedFormatsText = "Supported formats :",
    isCamera = true,
}: FileUploadProps) => {
    const [error, setError] = useState<string | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<string>("");
    const [capturedImage, setCapturedImage] = useState<string>("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [onEnter, setOnEnter] = useState<boolean>(false);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !onEnter) {
                setOnEnter(true);
                capturePhoto()
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onEnter]);

    useEffect(() => {
        if (isCamera && showCamera) getCameras();
    }, [isCamera, showCamera]);

    const getCameras = async () => {
        try {
            await navigator.mediaDevices.getUserMedia({ video: true });
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setCameras(videoDevices);
            if (videoDevices.length > 0) {
                setSelectedCamera(videoDevices[0].deviceId);
            }
        } catch (error) {
            setError("Cannot access camera. Please check permissions.");
        }
    };

    useEffect(() => {
        if (showCamera && selectedCamera) startCamera();
        return () => stopCamera();
    }, [showCamera, selectedCamera]);

    const startCamera = async () => {
        try {
            stopCamera();
            const constraints = {
                video: {
                    deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch (error) {
            setError("Failed to start camera. Please check permissions.");
            setShowCamera(false);
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) validateAndUploadFile(file);
    };

    const validateAndUploadFile = (file: File) => {
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            setError(`File size exceeds the maximum limit of ${maxSizeMB}MB`);
            return;
        }

        if (accept && accept !== "*") {
            const fileTypes = accept.split(",").map((type) => type.trim());
            const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
            const fileType = file.type;
            const isValidType = fileTypes.some((type) => {
                if (type.includes("/*")) {
                    const mainType = type.split("/")[0];
                    return fileType.startsWith(mainType);
                }
                return (
                    type === fileType || (type.startsWith(".") && type === fileExtension)
                );
            });

            if (!isValidType) {
                setError(`File type not accepted. Please upload: ${accept}`);
                return;
            }
        }
        setError(null);
        onChange(file);
    };

    const handleButtonClick = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    // const handleDeleteFile = () => {
    //     onChange(null);
    //     if (fileInputRef.current) fileInputRef.current.value = "";
    // };

    const handleCameraClose = () => {
        setShowCamera(false);
        setSelectedCamera('');
        stopCamera();
        setOnEnter(false);
    };

    const handlePreviewClose = () => {
        setShowPreview(false);
        setCapturedImage("");
        setOnEnter(false);
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context?.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            setCapturedImage(imageDataUrl);
            stopCamera();
            setShowCamera(false);
            setShowPreview(true);
        }
    };

    const submitCapturedPhoto = () => {
        if (capturedImage) {
            fetch(capturedImage)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
                        type: 'image/jpeg',
                        lastModified: Date.now(),
                    });
                    validateAndUploadFile(file);
                    handlePreviewClose();
                })
                .catch((error) => {
                    setError("Failed to process captured image " + error);
                    handlePreviewClose();
                });
            setOnEnter(false);
            setShowCamera(false);
        }
    };

    const retakePhoto = () => {
        handlePreviewClose();
        setShowCamera(true);
        setError(null);
        setOnEnter(false);
    };

    const fileName = value ? value.split("/").pop() : "";

    return (
        <div className={cn("w-full text-xs 2xl:text-sm", className)}>
            {label && (
                <Label
                    text={`${label}`}
                    required={required}
                    className="mb-1.5 2xl:mb-2"
                />
            )}

            <div className="flex">
                <div className="relative flex-grow">
                    <input
                        type="text"
                        className={cn(
                            "w-full rounded-l-lg border bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary",
                            disabled
                                ? "cursor-not-allowed bg-gray-100 text-gray-500"
                                : fileName
                                    ? "text-gray-900"
                                    : "text-gray-300",
                            isError || error ? "border-red-500" : "border-gray-300",
                            
                        )}
                        value={fileName || placeholder}
                        readOnly
                        disabled={disabled}
                        onClick={handleButtonClick}
                    />
                    {/* {
                        fileName && (
                            <div className="absolute right-0.5 top-1/2 flex -translate-y-1/2 gap-1">
                                {(
                                    <IconButton
                                        icon={<MdDeleteOutline className="h-4 w-4" />}
                                        onClick={handleDeleteFile}
                                        disabled={disabled}
                                        color="red"
                                    />
                                )}
                            </div>
                        )
                    } */}
                </div>

                <button
                    type="button"
                    onClick={handleButtonClick}
                    disabled={disabled}
                    className={cn("rounded-r-lg border border-l-0 px-4 py-2 font-medium transition duration-150 ease-in-out focus:outline-none", disabled
                        ? "bg-gray-300 text-gray-500 border-gray-200 cursor-not-allowed"
                        : "border-primary bg-primary text-white hover:border-indigo-800 hover:bg-indigo-800", className)}
                >
                    {buttonLabel}
                </button>

                {isCamera && (
                    <div className="pl-1 flex justify-center">
                        {
                            <IconButton
                                icon={<IoCameraOutline className="h-5 w-5" />}
                                onClick={() => {
                                    setShowCamera(true);
                                    setError(null);
                                }}
                                disabled={disabled}
                                color="blue"
                                className={fileName && isDelete ? "ml-1" : " hover:border-primary hover:bg-primary border border-primary bg-primary text-white"}
                            />
                        }
                    </div>
                )}

                <input
                    id={`file-upload-${label?.replace(/\s+/g, "-").toLowerCase()}`}
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept={accept}
                    disabled={disabled}
                    required={required}
                    className="hidden"
                />
            </div>

            <Modal isOpen={showCamera} onClose={handleCameraClose} size="md" title="Take a Photo">
                <div className="space-y-3 2xl:space-y-4">
                    {cameras && cameras.length > 1 && (
                        <SearchableDropdown
                            label="Select Camera:"
                            options={cameras.map((camera) => ({
                                label: camera.label || `Camera ${cameras.indexOf(camera) + 1}`,
                                value: camera.deviceId
                            }))}
                            value={selectedCamera}
                            onChange={(obj) => setSelectedCamera(obj.value)}
                        />
                    )}

                    <div className="relative bg-gray-50 rounded-lg overflow-hidden mb-3 2xl:mb-4">
                        <div className="relative z-10">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                className="w-full h-80 object-cover"
                            />
                            <canvas ref={canvasRef} className="hidden" />
                        </div>
                        <CamaraLoader />
                    </div>

                    <div>
                        <Button text="Capture" className="w-full" onClick={capturePhoto} />
                    </div>
                </div>
            </Modal>

            <Modal isOpen={showPreview} onClose={handlePreviewClose} size="md" title="Preview Photo">
                <div className="space-y-3 2xl:space-y-4">
                    <div className="relative bg-gray-50 rounded-lg overflow-hidden mb-1 2xl:mb-2">
                        {capturedImage && (
                            <img
                                src={capturedImage}
                                alt="Captured preview"
                                className="w-full h-96 object-cover bg-black"
                            />
                        )}
                    </div>
                    <div className="flex justify-center items-center gap-3 2xl:gap-4">
                        <Button
                            text="Retake"
                            onClick={retakePhoto}
                            color="gray"
                            variant="outlined"
                        />
                        <Button
                            text="Submit"
                            onClick={submitCapturedPhoto}
                        />
                    </div>
                </div>
            </Modal>

            {(isError && errorMessage) || error ? (
                <ErrorMsg text={errorMessage || error || ""} />
            ) : (
                <>
                    {
                        supportedFormatsText ? (
                            <div className="text-[10px] text-gray-400">
                                {supportedFormatsText} {accept.replace(/\*/g, "all")} (Max: {maxSizeMB}MB)
                            </div>

                        ) : (
                            <></>
                        )
                    }
                </>
            )}
        </div>
    );
};

export { FileUpload };

const CamaraLoader = () => {
    return (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
            <svg
                className="w-10 h-10 animate-spin text-gray-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                ></circle>
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                ></path>
            </svg>
        </div>
    );
};